import React, { useEffect, useState, useCallback } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import axios from '../../axios';

import { IMarketResearchTask } from '../../interfaces/domain';
import { Routes } from '../../classes/Routes';

import Alert from '../../components/UI/Alert/Alert';
import Button, { EButtonColor, EButtonSize } from '../../components/UI/Button/Button';
import Container from '../../components/UI/Container/Container';
import Spinner from '../../components/UI/Spinner/Spinner';
import Table from '../../components/UI/Table/Table';
import TableTh from '../../components/UI/Table/TableTh/TableTh';
import { useTableSort } from '../../components/UI/Table/useTableSort';
import { useAuthUser } from '../../hooks/useAuthUser';

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  DRAFT: { label: 'Luonnos', color: '#94a3b8' },
  GATEKEEPER_PROMPT_READY: { label: 'Portinvartija valmis', color: '#6366f1' },
  GATEKEEPER_DONE: { label: 'Portinvartija ok', color: '#3b82f6' },
  FACTORY_PROMPT_READY: { label: 'Tehdastyöläinen valmis', color: '#8b5cf6' },
  FACTORY_DONE: { label: 'Tehdastyöläinen ok', color: '#a855f7' },
  COMPLETED: { label: 'Valmis', color: '#22c55e' },
  NO_GO: { label: 'No-Go', color: '#ef4444' },
  DEEP_RESEARCH_IN_PROGRESS: { label: 'Deep Research', color: '#f59e0b' },
};

const MarketResearchTasks: React.FC<RouteComponentProps> = ({ history }) => {
  const [tasks, setTasks] = useState<IMarketResearchTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { sort, onSort, items: sortedTasks } = useTableSort({ data: tasks });
  const authUser = useAuthUser();

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get('/json/market-research');
      setTasks(response.data || []);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data || "Virhe ladattaessa tehtäviä");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const newHandler = () => {
    history.push(Routes.MARKET_RESEARCH_TASK("new"));
  };

  const handleDeleteTask = async (id: string) => {
    if (!window.confirm("Haluatko varmasti poistaa tämän markkinatutkimuksen? Kaikki siihen liittyvät raportit ja taustatehtävät poistetaan myös.")) return;
    setLoading(true);
    setError(null);
    try {
      await axios.post('/json/market-research', {
        action: 'delete-task',
        id: id
      });
      fetchTasks();
    } catch (err: any) {
      setError(err.response?.data || "Virhe poistettaessa markkinatutkimusta");
      setLoading(false);
    }
  };


  const rowClickHandler = (id: string) => {
    history.push(Routes.MARKET_RESEARCH_TASK(id));
  };

  if (authUser && !authUser.isPrimary) {
    return <Container><Alert>Ei käyttöoikeutta</Alert></Container>;
  }

  const renderStatusBadge = (status?: string) => {
    const info = STATUS_LABELS[status || 'DRAFT'] || STATUS_LABELS.DRAFT;
    return (
      <span style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: '12px',
        fontSize: '0.75rem',
        fontWeight: 700,
        color: '#fff',
        backgroundColor: info.color,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.5px',
      }}>
        {info.label}
      </span>
    );
  };

  return (
    <Container>
      {error && <Alert>{error}</Alert>}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2>Markkinatutkimus</h2>
        <Button onClick={newHandler}>Uusi Tutkimus</Button>
      </div>

      {loading ? (
        <Spinner />
      ) : tasks.length > 0 && sortedTasks ? (
        <Table hover sortContext={{ sort, onSort }}>
          <thead>
            <tr>
              <TableTh sort="name">Nimi</TableTh>
              <TableTh sort="slug">Polku (Slug)</TableTh>
              <TableTh sort="status">Tila</TableTh>
              <TableTh sort="updated">Päivitetty</TableTh>
              <TableTh>Toiminnot</TableTh>
            </tr>
          </thead>
          <tbody>
            {sortedTasks.map((task) => (
              <tr
                key={task.id}
                onClick={() => rowClickHandler(task.id)}
                style={{ cursor: 'pointer' }}
              >
                <td>{task.name}</td>
                <td>{task.slug || ""}</td>
                <td>{renderStatusBadge(task.status)}</td>
                <td>
                  {task.updated ? new Date(task.updated).toLocaleDateString("fi-FI") : ""}
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  <Button size={EButtonSize.SMALL} color={EButtonColor.DANGER} onClick={() => handleDeleteTask(task.id)}>
                    Poista
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <p>Ei tutkimuksia löytynyt.</p>

      )}
    </Container>
  );
};

export default MarketResearchTasks;
